import { useEffect, useState } from "react";
import type { publicOperatorAdmin } from "@kintone-storage/openapi-types";
import { apiClient } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { Label } from "@/components/ui/label";

type Preview = publicOperatorAdmin.components["schemas"]["StorageUploadLimitPreview"];
const MiB = 1024 ** 2;

/** Storage IDをkeyにして使い、別Storageへ確認状態を持ち越さない。 */
export function StorageUploadLimitForm({ storageId }: { storageId: string }) {
  const [editing, setEditing] = useState(false);
  const [currentValue, setCurrentValue] = useState<number | null>(null);
  const [value, setValue] = useState("");
  const [preview, setPreview] = useState<Preview | null>(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const path = `/storages/${encodeURIComponent(storageId)}/upload-limit`;
  useEffect(() => {
    let active = true;
    void apiClient<Preview>(path).then((current) => {
      if (active) {
        setCurrentValue(current.previousMaxUploadFileBytes / MiB);
        setValue(String(current.previousMaxUploadFileBytes / MiB));
      }
    }).catch(() => {
      if (active) setError("上限を取得できません。ストレージの設定所有者であることを確認し、画面を再読込してください。");
    }).finally(() => { if (active) setBusy(false); });
    return () => { active = false; };
  }, [path]);

  async function inspect() {
    setPreview(null);
    setSaved(false);
    const mib = Number(value);
    if (!/^\d+$/.test(value) || !Number.isSafeInteger(mib) || mib < 1 || mib > 10240) {
      setError("1〜10,240MiBの整数を必ず指定してください。");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const proposed = await apiClient<Preview>(`${path}?maxUploadFileBytes=${mib * MiB}`);
      if (proposed.affectedApps.length > 0) setPreview(proposed);
      else await save(proposed);
    } catch {
      setError("変更対象を確認できません。所有権・接続状態を確認して再度確認してください。");
    } finally { setBusy(false); }
  }
  async function save(confirmed: Preview) {
    setBusy(true);
    setError(null);
    try {
      const result = await apiClient<Preview>(path, { method: "PUT", body: JSON.stringify({
        maxUploadFileBytes: confirmed.maxUploadFileBytes, expectedRevision: confirmed.expectedRevision,
      }) });
      setValue(String(result.maxUploadFileBytes / MiB));
      setCurrentValue(result.maxUploadFileBytes / MiB);
      setEditing(false);
      setSaved(true);
    } catch {
      setError("保存を確認できません。設定変更との競合を含む可能性があります。変更対象を再確認してください。");
    } finally { setPreview(null); setBusy(false); }
  }
  return <section className="space-y-3 rounded-lg border p-4" aria-label="アップロード容量設定">
    {editing ? <>
      <Label htmlFor="storage-upload-limit">1ファイルの最大サイズ（MiB）</Label>
      <Input id="storage-upload-limit" className="max-w-xs" autoFocus value={value} inputMode="numeric" disabled={busy}
        onChange={(event) => { setValue(event.target.value); setPreview(null); setSaved(false); setError(null); }} />
      <p className="text-sm text-muted-foreground">1〜10,240MiBの整数で入力してください。</p>
    </> : <div className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <p className="text-sm text-muted-foreground">1ファイルの最大サイズ</p>
        <p className="text-lg font-semibold">{currentValue === null ? (busy ? "読み込み中…" : "取得できませんでした") : `${currentValue.toLocaleString("ja-JP")} MiB`}</p>
      </div>
      <Button type="button" variant="outline" disabled={busy || currentValue === null} onClick={() => { setEditing(true); setSaved(false); setError(null); }}>変更</Button>
    </div>}
    <p className="text-sm text-muted-foreground">最大サイズはアプリごとにも設定できますが、このストレージの最大サイズを超えることはできません。</p>
    {error && <p role="alert">{error}</p>}
    {saved && <p role="status">保存しました。新規アップロードに適用します。プラグインの表示更新には設定の再反映が必要です。</p>}
    {editing && <Button type="button" disabled={busy || preview !== null} onClick={() => void inspect()}>保存</Button>}
    <ConfirmDialog
      open={preview !== null}
      onClose={() => setPreview(null)}
      onConfirm={() => preview ? save(preview) : undefined}
      title="アプリのアップロード上限も変更します"
      confirmText="保存"
      isLoading={busy}
      description={preview && <div className="space-y-3">
        <p>ストレージと次の{preview.affectedApps.length}アプリの上限を{(preview.maxUploadFileBytes / MiB).toLocaleString("ja-JP")} MiBに変更します。</p>
        <ul className="max-h-60 overflow-y-auto list-disc pl-5">{preview.affectedApps.map((app) => <li key={app.appBindingId}>{app.kintoneAppName}（アプリ{app.kintoneAppId}）</li>)}</ul>
        <p>引き下げたアプリの個別上限は、後でストレージの上限を上げても元に戻りません。</p>
      </div>}
    />
    {editing && <Button type="button" variant="outline" disabled={busy || preview !== null} onClick={() => {
      setValue(String(currentValue)); setEditing(false); setPreview(null); setError(null);
    }}>キャンセル</Button>}
  </section>;
}
